import { Dialog } from '@angular/cdk/dialog';
import { FlexibleConnectedPositionStrategyOrigin, Overlay } from '@angular/cdk/overlay';
import { effect, inject, Injectable } from '@angular/core';
import { FormControl } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import * as joint from '@clientio/rappid';
import { TranslocoService } from '@jsverse/transloco';
import { MedicalComponentSearchPopoverComponent, MedicalComponentSearchPopoverData } from 'guideline/feature-component-search';
import { LegacySidebarService } from 'guideline/feature-sidebar';
import interact from 'interactjs';
import { FlowchartSidebarStoreService } from 'libs/guideline/feature-sidebar/src/lib/infrastructure/flowchart-sidebar-store.service';
import { cloneDeep, debounce } from 'lodash-es';
import { filter } from 'rxjs';
import {
  findFlowchartElementRecursive,
  GuidelineService,
  LinksService,
  mapMimeTypeToAttachmentType,
  ScrollService,
  SelectionService,
} from 'shared/domain';
import { NodeDetailPopoverComponent } from 'shared/feature-node-detail-popover';
import { ConfirmationDialogArgs, ConfirmationDialogComponent, DialogRef, DialogService, PopoverService } from 'shared/ui-common';

import { AnchoredComment, CommentService } from '@treetop/shared/comments';
import {
  Attachment,
  FlowchartActionDto,
  FlowchartContainerDto,
  FlowchartDecisionNodeDto,
  FlowchartElementType,
  FlowchartLinkDto,
  FlowchartNodeDto,
  FlowchartStartNodeDto,
  FlowchartSubflowDto,
  FlowchartTextBlockDto,
  GuidelineActionDto,
  GuidelineActionSignificance,
  NodeDetailPopoverData,
  NodeDetailPopoverTab,
  TimeSpanDto,
} from '@treetop/shared/model';
import { ReferenceNumberingService } from '@treetop/shared/reference-numbering';

import { FlowchartSearchService } from './flowchart-search.service';
import { FlowchartConfig } from './flowchart.config';
import { CommentType } from '../../../../../tt-comments/src/lib/comment.model';
import { AddCommentPopoverComponent } from '../../../../../tt-comments/src/lib/components/add-comment-popover/add-comment-popover.component';
import { AddButtonOptions, AddToolbarPopoverComponent } from '../../components/add-toolbar-popover/add-toolbar-popover.component';
import { ElementClasses } from '../../model/element-classes.model';
import { markLinkAsSelected, unselectAllLinks } from '../../utils/links';
import { FlowchartElementsSubjectStore } from '../data/flowchart-elements-subject-store.service';
import { FlowchartManagerService } from '../data/flowchart-manager.service';
import { BaseGraph } from '../flowchart/graph/BaseGraph';
import { FlowchartModelService } from '../flowchart/model.service';
import { BasePaper } from '../flowchart/paper/BasePaper';
import { FlowchartScrollerService } from '../flowchart/scroller.service';
import { FlowchartViewService } from '../flowchart/view.service';
import {
  generateBottomButton,
  generateContainerAddButton,
  generateLeftButton,
  generateResetButtonRegular,
  generateResetButtonStart,
  generateRightButton,
  generateSubflowAddButton,
  generateTopButton,
} from '../operations/button-tools-generator.util';
import { ContainerAttributes } from '../operations/container/container-attributes';
import { ContainerResize } from '../operations/container/ContainerResize';
import { getHeightOfSubComponent } from '../operations/container/ContainerSubComponentCalculator';
import { ContainerNodeService } from '../operations/container-node.service';
import { ElementActivitiesToolbarRef } from '../operations/element-activities-toolbar/element-activities-toolbar-ref';
import ElementActivitiesTransactionService from '../operations/element-activities-toolbar/element-activities-transaction.service';
import { FlowchartLoaderService } from '../operations/flowchart-loader.service';
import { getGridMatchedPositionValue, snapCenterOfElementToGrid } from '../operations/grid.util';
import { LinkToolGenerator } from '../operations/link/adjust-tool/LinkToolGenerator';
import { AttributePositionLogic } from '../operations/link/attribute/AttributePositionLogic';
import { LinkValidator } from '../operations/link/validator/LinkValidator';
import NodePickerTransactionService from '../operations/node-picker-toolbar/node-picker-transaction.service';
import OperationsService from '../operations/operations.service';
import SelectionBehaviorService from '../operations/selection-behavior.service';
import { SizeService } from '../operations/size.service';
import { SelectionBehaviorElement, SelectionBehaviorElementView } from '../shapes/selection-behavior';

export type FlowchartGenericDto = FlowchartStartNodeDto &
  FlowchartTextBlockDto &
  FlowchartActionDto &
  FlowchartDecisionNodeDto &
  FlowchartContainerDto &
  FlowchartSubflowDto &
  FlowchartNodeDto &
  FlowchartLinkDto;

@Injectable()
export class FlowchartEventsService {
  private dialog = inject(MatDialog);
  private elementActivitiesTransactionService = inject(ElementActivitiesTransactionService);
  private flowchartLoaderService = inject(FlowchartLoaderService);
  private flowchartManagerService = inject(FlowchartManagerService);
  private flowchartModelService = inject(FlowchartModelService);
  private flowchartScrollerService = inject(FlowchartScrollerService);
  private flowchartSearchService = inject(FlowchartSearchService);
  private flowchartSidebarStoreService = inject(FlowchartSidebarStoreService);
  private flowchartViewService = inject(FlowchartViewService);
  private linksService = inject(LinksService);
  private nodePickerTransactionService = inject(NodePickerTransactionService);
  private operationsService = inject(OperationsService);
  private popoverService = inject(PopoverService);
  private router = inject(Router);
  private selectionBehaviorService = inject(SelectionBehaviorService);
  private legacySidebarService = inject(LegacySidebarService);
  private sizeService = inject(SizeService);
  private translocoService = inject(TranslocoService);
  private commentService = inject(CommentService, { optional: true });
  private guidelineService = inject(GuidelineService);
  private referenceNumberingService = inject(ReferenceNumberingService);
  private selectionService = inject(SelectionService);

  private flowchartElementsSubjectStore = inject(FlowchartElementsSubjectStore);

  private cdkDialog = inject(Dialog);
  private cdkOverlay = inject(Overlay);
  private scrollService = inject(ScrollService);

  public selection: joint.ui.Selection;

  private paperSource: BasePaper;
  private graphSource: BaseGraph;
  private scrollerSource: joint.ui.PaperScroller;
  private selectedElement;
  private keyboard: joint.ui.Keyboard;
  private scrollChanged: boolean;
  private lastScrollY: number;
  private scrollInterval: number;
  private toolbarScrollInterval: any;
  private isSmartbookPage: boolean =
    this.router.url.includes('guideline') &&
    !this.router.url.includes('/add') &&
    !this.router.url.includes('/edit') &&
    !this.router.url.includes('/manage') &&
    !this.router.url.includes('/preview');
  private selectedLink: joint.dia.Link = null;
  private selectedCell: joint.dia.CellView = null;
  private linkTools: joint.dia.ToolsView;
  private editorTooltip: joint.ui.Tooltip = null;
  private upArrowTooltip: joint.ui.Tooltip = null;
  private downArrowTooltip: joint.ui.Tooltip = null;
  private currentElementActivityToolbarRef: ElementActivitiesToolbarRef = null;
  private resizeTool: joint.ui.FreeTransform;
  private linkDeleteConfirmationDialgRef: DialogRef<boolean>;

  private boundKeydownEventHandler: (event: KeyboardEvent) => void;
  private boundTouchMoveEventHandler: (event: TouchEvent) => void;
  private boundScrollEventHandler: () => void;
  private boundSecondaryKeydownEventHandler: (event: KeyboardEvent) => void;
  private boundCtrlPlusEventHandler: (event: any) => void;
  private boundCtrlMinusEventHandler: (event: any) => void;

  private flowchartId: string;

  private dialogService = inject(DialogService);

  private debouncedAddElementTags = debounce((dto: any) => {
    this.operationsService.addElementTags(dto);
  }, 150);

  constructor() {
    effect(() => {
      const flowchartElementId = this.scrollService.flowchartElementToHighlight();
      if (flowchartElementId) {
        const element = this.legacySidebarService.deepSearchElements(this.flowchartElementsSubjectStore.getValue(), flowchartElementId);
        if (element) {
          const elementView = this.paperSource.paper.findViewByModel(flowchartElementId);
          const flowchartElement = this.paperSource.paper.model.getElements().find((el) => el.id === element.id);
          const highlightClass = 'highlighted-flowchart-element';
          elementView.$el.find('[joint-selector="body"]').addClass(highlightClass);
          this.scrollerSource.scrollToElement(flowchartElement, { animation: { duration: 200 } });
          setTimeout(() => {
            elementView.$el.find('[joint-selector="body"]').removeClass(highlightClass);
            this.scrollService.flowchartElementToHighlight.set('');
          }, 3000);
        }
      }
    });

    this.paperSource = this.flowchartViewService.getPaper();
    this.graphSource = this.flowchartModelService.getGraph();
    this.scrollerSource = this.flowchartScrollerService.getScroller();
    this.keyboard = new joint.ui.Keyboard();
    this.keyboard.disable();
    this.subscribeToComponentAddedExecution();

    // Event handler bindings
    this.boundKeydownEventHandler = this.keydownEventHandler.bind(this);
    this.boundTouchMoveEventHandler = this.touchMoveEventHandler.bind(this);
    this.boundCtrlPlusEventHandler = this.ctrlPlusEventHandler.bind(this);
    this.boundCtrlMinusEventHandler = this.ctrlMinusEventHandler.bind(this);
    this.boundSecondaryKeydownEventHandler = this.secondaryKeyDownEventHandler.bind(this);
  }

  public init(flowchartId: string) {
    this.flowchartId = flowchartId;
  }

  public initializeEventHandlers(options?: { allowEvents?: boolean; allowInlineComments?: boolean }): void {
    if (options.allowEvents) {
      this.removeEventHandlers();

      this.flowchartLoaderService.unselectFlowchartElmements.subscribe(() => {
        this.handleBlankPointerClick();
      });

      this.keyboard.enable();

      this.editorTooltip = new joint.ui.Tooltip({
        rootTarget: document.body,
        target: '[data-tooltip-reset]',
        dataAttributePrefix: 'data-tooltip-reset',
        trigger: 'hover',
        position: joint.ui.Tooltip.TooltipPosition.Bottom,
        padding: 4,
        content: (target: HTMLElement) => target.getAttribute('tooltip'),
      });

      this.upArrowTooltip = new joint.ui.Tooltip({
        rootTarget: document.body,
        target: `.joint-cell[data-arrow-type="${ContainerAttributes.IsSubComponentUpArrow}"]`,
        trigger: 'hover',
        position: joint.ui.Tooltip.TooltipPosition.Left,
        padding: 4,
        content: this.translocoService.translate('Flowchart.MoveElementUp'),
      });

      this.downArrowTooltip = new joint.ui.Tooltip({
        rootTarget: document.body,
        target: `.joint-cell[data-arrow-type="${ContainerAttributes.IsSubComponentDownArrow}"]`,
        trigger: 'hover',
        position: joint.ui.Tooltip.TooltipPosition.Left,
        padding: 4,
        content: this.translocoService.translate('Flowchart.MoveElementDown'),
      });

      this.paperSource.paper.options.linkPinning = false;
      this.paperSource.paper.options.defaultLink = this.operationsService.createDefaultLink();

      this.initializeZoomEvents();

      document.addEventListener('scroll', this.boundScrollEventHandler, true);

      this.paperSource.paper.on({
        'blank:pointerdown': this.handleBlankPointerDown,
        'blank:pointerclick': this.handleBlankPointerClick,
        'blank:pointermove': this.handleBlankPointerMove,
        'element:pointerdown': this.handleElementPointerDown,
        'element:pointermove': this.handleElementPointerMove,
        'element:magnet:pointerdown': this.handleElementMagnetPointerDown,
        'element:magnet:pointerup': this.handleElementMagnetPointerUp,
        'element:mouseenter': this.handleElementMouseEnter,
        'element:mouseleave': this.handleElementMouseLeave,
        'element:pointerclick': (cellView: joint.dia.CellView) => this.handleElementPointerClick(cellView, options.allowInlineComments),
        'element:pointerup': this.handleElementPointerUp,
        'cell:pointermove': this.handleCellPointerMove,
        'cell:pointerclick': this.handleCellClicked,
        'cell:mouseover': this.handleMouseOver,
        'cell:mouseout': this.handleMouseOut,
        'selectionBehavior:open': this.handleSelectionBehaviorOpen,
        'selectionBehavior:close': this.handleSelectionBehaviorClose,
        'link:pointerup': this.handleLinkPointerUp,
        'link:pointerclick': (linkView: joint.dia.LinkView) => this.handleLinkPointerClick(linkView, options.allowInlineComments),
        'link:pointermove': this.handlePointerMove,
        'link:connect': this.handleLinkConnect,
        'cell:pointerdown': this.handleCellPointerDown,
        'cell:pointerup': this.handleCellPointerUp,
      });

      document.addEventListener('keydown', this.boundKeydownEventHandler);
      document.addEventListener('keydown', this.boundSecondaryKeydownEventHandler);
    } else if (this.commentService) {
      this.paperSource.paper.on('element:pointerclick', (cellView: joint.dia.CellView) =>
        this.handleReadonlyCommentClick(cellView, options.allowInlineComments),
      );
      this.paperSource.paper.on('link:pointerclick', (linkView: joint.dia.LinkView) =>
        this.handleReadonlyCommentClick(linkView, options.allowInlineComments),
      );
    }
  }

  public removeEventHandlers() {
    this.selectedLink = null;
    this.selectedCell = null;
    this.selectedElement = null;

    this.keyboard.disable();

    this.paperSource.paper.off('blank:pointerdown');
    this.paperSource.paper.off('blank:pointerclick');
    this.paperSource.paper.off('element:pointerdown');
    this.paperSource.paper.off('element:magnet:pointerdown');
    this.paperSource.paper.off('element:magnet:pointerup');
    this.paperSource.paper.off('element:mouseenter');
    this.paperSource.paper.off('element:mouseleave');
    this.paperSource.paper.off('element:pointerclick');
    this.paperSource.paper.off('element:pointermove');
    this.paperSource.paper.off('element:pointerup');
    this.paperSource.paper.off('paper:mousewheel');
    this.paperSource.paper.off('selectionBehavior:open');
    this.paperSource.paper.off('selectionBehavior:close');
    this.paperSource.paper.off('cell:pointerclick');
    this.paperSource.paper.off('link:pointerup');
    this.paperSource.paper.off('link:pointerclick');
    this.paperSource.paper.off('link:connect');
    this.paperSource.paper.off('link:pointermove');
    this.paperSource.paper.off('cell:pointerdown');
    this.paperSource.paper.off('cell:pointerup');

    this.paperSource.paper.el.removeEventListener('touchmove', this.boundTouchMoveEventHandler);

    document.removeEventListener('keydown', this.boundKeydownEventHandler);
    document.removeEventListener('keydown', this.boundSecondaryKeydownEventHandler);

    this.keyboard.off('ctrl+=', this.boundCtrlPlusEventHandler);
    this.keyboard.off('ctrl+-', this.boundCtrlMinusEventHandler);

    if (this.editorTooltip) {
      this.editorTooltip.remove();
    }

    if (this.upArrowTooltip) {
      this.editorTooltip.remove();
    }

    if (this.downArrowTooltip) {
      this.editorTooltip.remove();
    }

    if (this.scrollInterval) {
      clearInterval(this.scrollInterval);
    }
  }

  clearSelection() {
    //close selection behavior
    const selectionBehaviorNodes = this.paperSource.paper.model.getElements().filter((element) => {
      return element.attributes.attrs.body?.class === 'element--selectionbehavior__body';
    });
    selectionBehaviorNodes.forEach((element) => {
      if (element.attributes.type === 'treetop.SelectionBehavior') {
        (element as SelectionBehaviorElement).closeCaret();
      } else {
        element.prop('attrs/image/xlink:href', '/assets/icons/chevron-down.svg');
      }
    });
  }

  public preventInteractiveNodes(mobileView = false) {
    new joint.ui.Tooltip({
      rootTarget: this.paperSource.paper.el,
      target: '[data-tooltip]',
      position: joint.ui.Tooltip.TooltipPosition.Left,
      padding: 10,
      content: (element: HTMLElement) => {
        const lastTextElement = element.querySelector(`.${ElementClasses.ElementText}`).lastElementChild;
        if (!lastTextElement.textContent.endsWith('…')) {
          return false;
        }

        return `
        <div style="max-width: 200px; margin: 0; white-space: pre-wrap; overflow-wrap: break-word">${element.getAttribute('data-tooltip')}</div>
        `;
      },
    });

    this.paperSource.paper.on('render:done', () => {
      const flowchartId = this.flowchartScrollerService.flowchartId;
      const scrollRegisterFlowchartId = flowchartId.endsWith('-flowchart') ? flowchartId.slice(0, -'--flowchart'.length) : flowchartId;
      this.scrollService.setFlowchartLoaded(scrollRegisterFlowchartId);
    });

    let pointerDownPosition: { x: number; y: number } | null = null;

    if (!mobileView) {
      this.paperSource.paper.on('blank:pointerdown', (event) => {
        this.scrollerSource.startPanning(event);
        this.stopInteractionIfGestureDetected({ event });
      });
    }

    this.paperSource.paper.on('element:pointerdown', (cellView, event) => {
      this.scrollerSource.startPanning(event);
      this.stopInteractionIfGestureDetected({ cellView, event });
      pointerDownPosition = { x: event.clientX, y: event.clientY };
    });

    this.paperSource.paper.on('element:pointerup', (_cellView, evt) => {
      if (_cellView.model.attributes.type === 'example.ForeignObject') return;

      if (!pointerDownPosition) return;

      const dx = Math.abs(evt.clientX - pointerDownPosition.x);
      const dy = Math.abs(evt.clientY - pointerDownPosition.y);
      const movementThreshold = 5; //PX

      pointerDownPosition = null;

      // Only proceed if this was a "click" (not a drag/pan)
      if (dx > movementThreshold || dy > movementThreshold) return;
      const elementId = _cellView.model.attributes.id;
      let element = this.legacySidebarService.deepSearchElements(this.flowchartElementsSubjectStore.getValue(), elementId);
      if (!element) {
        element = this.legacySidebarService.deepSearchElements(
          this.flowchartElementsSubjectStore.getValue(),
          _cellView.model.attributes.parent,
        );
      }

      const paperOffset = _cellView.paper.$el.offset();
      const point: FlexibleConnectedPositionStrategyOrigin = {
        x: _cellView.getBBox().x + paperOffset.left,
        y: _cellView.getBBox().y + paperOffset.top,
        width: _cellView.getBBox().width,
        height: _cellView.getBBox().height,
      };

      const flowchartDialogData = this.getFlowchartDialogData(_cellView, element);
      if (flowchartDialogData) {
        flowchartDialogData.links = element.links;

        let selectedTab: NodeDetailPopoverTab;
        if (_cellView.model.attributes.isAttachment) {
          selectedTab = NodeDetailPopoverTab.ATTACHMENTS;
        } else if (_cellView.model.attributes.isLink) {
          selectedTab = NodeDetailPopoverTab.LINKS;
        } else if (_cellView.model.attributes.isMedication) {
          selectedTab = NodeDetailPopoverTab.MEDICATIONS;
        } else if (_cellView.model.attributes.isDescription && !_cellView.model.attributes.anchorLinkClicked) {
          selectedTab = NodeDetailPopoverTab.DESCRIPTION;
        } else if (_cellView.model.attributes.isReferences) {
          selectedTab = NodeDetailPopoverTab.REFERENCES;
        }

        const description = flowchartDialogData.showDescriptionTab
          ? {
              description: flowchartDialogData.description,
              recurrence: flowchartDialogData.recurrence,
              recurrenceInterval: flowchartDialogData.recurrenceInterval,
            }
          : undefined;

        const references = this.referenceNumberingService
          .orderedReferences()
          .filter((reference) => element.references?.map((ref) => ref.id).includes(reference.id));

        const nodeDetailPopoverData: NodeDetailPopoverData = {
          nodeTitle: flowchartDialogData.title,
          links: flowchartDialogData.links,
          description,
          attachments: flowchartDialogData.attachments,
          medications: flowchartDialogData.medicationRequests,
          references: references,
          selectedTab,
          flowchartElement: element,
        };

        const providers = [
          { provide: ReferenceNumberingService, useValue: this.referenceNumberingService },
          { provide: ScrollService, useValue: this.scrollService },
          { provide: LinksService, useValue: this.linksService },
        ];

        const hasContent =
          flowchartDialogData?.showDescriptionTab ||
          flowchartDialogData?.attachments?.length > 0 ||
          flowchartDialogData?.medicationRequests?.length > 0 ||
          flowchartDialogData?.links?.length > 0 ||
          references?.length > 0;

        if (hasContent) {
          if (window.innerWidth < 480) {
            this.cdkDialog.open(NodeDetailPopoverComponent, {
              width: '100vw',
              maxWidth: '100vw',
              data: nodeDetailPopoverData,
              autoFocus: 'none',
              positionStrategy: this.cdkOverlay.position().global().bottom(),
              providers,
            });
          } else {
            this.popoverService.open(NodeDetailPopoverComponent, point, {
              data: nodeDetailPopoverData,
              positionStrategy: 'top-right',
              offsetY: 8,
              providers,
            });
          }
        }
      }

      _cellView.preventDefaultInteraction(evt);
    });

    this.paperSource.paper.on('cell:pointerdown', (cellView, event) => {
      this.handleCellPointerDown(cellView, event);
      this.stopInteractionIfGestureDetected({ cellView, event });
    });

    this.paperSource.paper.on('cell:pointerup', (cellView) => {
      this.handleCellPointerUp(cellView);
    });

    this.paperSource.paper.el.addEventListener('touchmove', this.boundTouchMoveEventHandler, { passive: false });

    let initialOrigin = null;
    let initialZoom = null;
    const _this = this;

    // Zoom and scroll with gestures on mobile using interact.js
    interact(this.scrollerSource.el).gesturable({
      onstart: onGestureStart,
      onmove: onGestureMove,
      onend: onGestureEnd,
    });

    function onGestureStart(evt) {
      const { box } = evt;
      initialZoom = _this.scrollerSource.zoom();
      initialOrigin = _this.paperSource.paper.clientToLocalPoint(new joint.g.Rect(box).center().round());
    }

    function onGestureMove(evt) {
      const { box, scale } = evt;

      const gestureCenter = new joint.g.Rect(box).center();
      const origin = _this.paperSource.paper.clientToLocalPoint(gestureCenter).round();

      // Scroll
      _this.scrollerSource.positionPoint(initialOrigin, gestureCenter.x, gestureCenter.y);

      // Zoom
      const z = initialZoom * scale;
      _this.scrollerSource.zoom(z, {
        absolute: true,
        min: FlowchartConfig.paper.ZOOM_MIN,
        max: FlowchartConfig.paper.ZOOM_MAX,
        ox: origin.x,
        oy: origin.y,
      });
    }

    function onGestureEnd() {
      initialOrigin = null;
      initialZoom = null;
    }

    this.initializeZoomEvents();
  }

  private stopInteractionIfGestureDetected({ cellView, event }: { cellView?: any; event: any }): void {
    if ((event.type === 'touchstart' || event.type === 'touchmove') && event.originalEvent.touches.length > 1) {
      if (cellView) cellView.preventDefaultInteraction(event);
      this.scrollerSource.stopPanning(event);
    }
  }

  private getFlowchartDialogData(_cellView, element) {
    if (!element) {
      return null;
    }

    let title: string;
    let description: string;
    let attachments: Attachment[];
    let medicationRequests: GuidelineActionDto[];
    let preferredOptionOrder: number;
    let significance: GuidelineActionSignificance;
    let recurrence: number;
    let recurrenceInterval: TimeSpanDto;

    if (_cellView.model.attributes.isHeader) {
      const action = element as FlowchartActionDto;
      title = action?.guidelineAction?.title;
      description = action?.guidelineAction?.description !== undefined ? action.guidelineAction.description : null;
      attachments = action?.attachments;
    } else if (element.elementType === FlowchartElementType.FlowchartStartNode) {
      const start = element as FlowchartStartNodeDto;
      ({ title, description } = start.guidelineAction || start.guidelineCriteria);
      attachments = start.attachments;
    } else if (element.elementType === FlowchartElementType.FlowchartAction) {
      const action = element as FlowchartActionDto;
      ({ title, description, medicationRequests, recurrence, recurrenceInterval, significance, preferredOptionOrder } =
        action.guidelineAction);
      attachments = action.attachments;
    } else if (element.elementType === FlowchartElementType.FlowchartDecisionNode) {
      const decision = element as FlowchartDecisionNodeDto;
      if (decision.rule) {
        title = decision.ruleTitle;
        description = decision.ruleDescription;
      } else {
        ({ title, description } = decision.guidelineCriteria || decision.guidelineObservation);
        attachments = decision.attachments;
      }
    } else if (element.elementType === FlowchartElementType.FlowchartTextBlock) {
      const textblock = element as FlowchartTextBlockDto;
      attachments = textblock.attachments;
      description = textblock.content;
    } else if (element.elementType === FlowchartElementType.FlowchartContainer) {
      const container = element as FlowchartContainerDto;

      description = container.guidelineAction?.description !== undefined ? container.guidelineAction.description : null;
      ({ title, medicationRequests, recurrence, recurrenceInterval, significance, preferredOptionOrder } = container.guidelineAction);
      attachments = container.attachments;
    } else if (element.elementType === FlowchartElementType.FlowchartSubflow) {
      const subflow = element as FlowchartSubflowDto;
      description = subflow.guidelineAction?.description !== undefined ? subflow.guidelineAction.description : null;
      ({ title, medicationRequests, recurrence, recurrenceInterval, significance, preferredOptionOrder } = subflow.guidelineAction);
      attachments = subflow.attachments;
    }

    const hasRecurrence = !!recurrence || !!recurrenceInterval;
    const canShowDescription = description && !element.showDescription && element.elementType !== FlowchartElementType.FlowchartTextBlock;

    const showDescriptionTab = hasRecurrence || canShowDescription;

    return {
      title,
      description,
      attachments: attachments?.map((attachment) => ({
        id: attachment.id,
        title: attachment.displayName,
        fileType: mapMimeTypeToAttachmentType(attachment.contentType),
        updatedAtTimestamp: attachment.updatedAt || attachment.createdAt,
      })),
      medicationRequests,
      preferred: !preferredOptionOrder,
      optional: significance === GuidelineActionSignificance.Optional,
      recurrence,
      recurrenceInterval,
      links: [],
      note: null,
      showDescriptionTab,
    };
  }

  private initializeZoomEvents() {
    this.keyboard.on({
      'ctrl+=': this.boundCtrlPlusEventHandler,
      'ctrl+-': this.boundCtrlMinusEventHandler,
    });

    this.paperSource.paper.on('paper:mousewheel', (evt, _x, _y, delta) => {
      if (evt.ctrlKey) {
        evt.preventDefault();
        const oldscale = this.paperSource.paper.scale().sx;
        const newscale = oldscale + 0.2 * delta * oldscale;

        if (newscale > FlowchartConfig.paper.ZOOM_MIN && newscale < FlowchartConfig.paper.ZOOM_MAX) {
          this.paperSource.paper.scale(newscale);
        } else if (newscale > FlowchartConfig.paper.ZOOM_MAX) {
          this.paperSource.paper.scale(FlowchartConfig.paper.ZOOM_MAX);
        } else if (newscale < FlowchartConfig.paper.ZOOM_MIN) {
          this.paperSource.paper.scale(FlowchartConfig.paper.ZOOM_MIN);
        }
      }
    });
  }

  closeToolbar = () => {
    joint.ui.ContextToolbar.close();
  };

  private scrollEventHandler = () => {
    this.scrollChanged = true;
  };

  private setScrollIntervalForToolbar() {
    const guidelineScroller = document.getElementById('tt-main-sidebar_content');
    this.lastScrollY = guidelineScroller.scrollTop;
    this.scrollChanged = false;

    this.toolbarScrollInterval = setInterval(() => {
      if (this.scrollChanged) {
        const currentScrollY: number = guidelineScroller.scrollTop;
        if (currentScrollY !== this.lastScrollY) {
          if (currentScrollY > this.lastScrollY + 50) {
            this.closeToolbar();
            if (this.toolbarScrollInterval) {
              clearInterval(this.toolbarScrollInterval);
            }
          }
          this.lastScrollY = currentScrollY;
        }
        this.scrollChanged = false;
      }
    }, 500);
  }

  private handleCommentSelect(elementView: joint.dia.CellView | joint.dia.LinkView) {
    elementView = this.resolveCommentElementView(elementView);
    const elementId = this.getCommentElementId(elementView);
    if (!elementId) {
      console.debug('FlowchartEventsService: No element ID found for comment selection.');
      return;
    }

    this.commentService.selectCommentByFlowchartElementId(elementId, this.flowchartId);
  }

  private handleReadonlyCommentClick(elementView: joint.dia.CellView | joint.dia.LinkView, allowInlineComments: boolean) {
    if (!this.commentService?.initialized()) {
      return;
    }

    if (allowInlineComments && !this.commentService.readonly()) {
      this.handleCommentPopover(elementView);
      return;
    }

    this.handleCommentSelect(elementView);
  }

  private handleCommentPopover(elementView: joint.dia.CellView | joint.dia.LinkView) {
    if (!this.commentService?.initialized() || this.commentService.readonly()) {
      return;
    }

    elementView = this.resolveCommentElementView(elementView);

    if (elementView.model.attributes.type === 'treetop.SelectionBehavior') {
      return;
    }

    const elementId = this.getCommentElementId(elementView);
    if (!elementId) {
      console.debug('FlowchartEventsService: No element ID found for comment creation.');
      return;
    }

    const comments = this.commentService
      .comments()
      .filter((comment) => comment.anchor.elementId === elementId && comment.anchor.widgetId === this.flowchartId);

    if (comments.length) {
      const selectedCommentId = this.commentService.selectedComment()?.comment.id;
      const selectedIndex = comments.findIndex((comment) => comment.id === selectedCommentId);

      if (selectedIndex < 0 || selectedIndex < comments.length - 1) {
        this.commentService.selectCommentByCommentId(comments[Math.max(0, selectedIndex + 1)].id);
        return;
      }

      const allowedTypes = this.commentService.getNewCommentTypes();
      const missingTypes = allowedTypes.filter((type) => !comments.some((comment) => comment.type === type));
      if (allowedTypes.length > 1 && missingTypes.length) {
        this.openCommentPopover(elementView, elementId, missingTypes);
        return;
      }

      this.commentService.selectCommentByCommentId(comments[0].id);
      return;
    }

    this.openCommentPopover(elementView, elementId, this.commentService.getNewCommentTypes());
  }

  private openCommentPopover(
    elementView: joint.dia.CellView | joint.dia.LinkView,
    elementId: string,
    allowedTypes: CommentType[],
  ) {
    if (allowedTypes.length === 0) {
      return;
    }

    const paperOffset = elementView.paper.$el.offset();
    const absolutePosX = elementView.getBBox().center().x + paperOffset.left;
    const absolutePosY = elementView.getBBox().topMiddle().y + paperOffset.top;
    const point: FlexibleConnectedPositionStrategyOrigin = {
      x: absolutePosX,
      y: absolutePosY,
      width: 0,
      height: 0,
    };

    this.popoverService
      .open(AddCommentPopoverComponent, point, {
        data: { allowedTypes },
        positionStrategy: 'top-center',
        scrollStrategy: 'close',
      })
      .afterClosed()
      .pipe(filter((type): type is CommentType => type !== undefined && type !== null))
      .subscribe((type: CommentType) => {
        const newComment: AnchoredComment = {
          id: crypto.randomUUID(),
          text: '',
          type,
          anchor: {
            type: 'flowchart-element',
            widgetId: this.flowchartId,
            elementId,
          },
        };

        this.commentService.addComment(newComment);
      });
  }

  private resolveCommentElementView(elementView: joint.dia.CellView | joint.dia.LinkView): joint.dia.CellView | joint.dia.LinkView {
    if (!elementView.model.attributes.isTextBlockContent) {
      return elementView;
    }

    return this.paperSource.paper.findViewByModel(elementView.model.attributes.parent) ?? elementView;
  }

  private getCommentElementId(elementView: joint.dia.CellView | joint.dia.LinkView): string | undefined {
    const elementId =
      elementView.model.prop('attrs/body/unique-id') ?? elementView.model.prop('attrs/line/uniqueId') ?? elementView.model.id;
    return elementId ? String(elementId) : undefined;
  }

  private handleBlankPointerMove = (event: joint.dia.Event) => {
    this.stopInteractionIfGestureDetected({ event });
  };

  private handlePointerMove = (cellView: joint.dia.CellView) => {
    cellView.hideTools();
  };

  private handleCellPointerMove = (cellView: joint.dia.CellView) => {
    //move rectangle together with foreign object
    if (cellView.model.attributes.isTextBlockContent) {
      const cell = this.graphSource.graph.getCell(cellView.model.attributes.parent);

      const cellInitialPosition = cell.position();
      cell.set('position', {
        x: cellView.model.attributes.position.x,
        y: cellView.model.attributes.position.y - FlowchartConfig.textBlockNode.offsetTop,
      });
      const cellFinalPosition = cell.position();

      const tags = cell.getEmbeddedCells().filter((el) => el.attributes.isElementTag) as joint.dia.Element[];
      tags.forEach((tag) => {
        tag.set('position', {
          x: tag.attributes.position.x + (cellFinalPosition.x - cellInitialPosition.x),
          y: tag.attributes.position.y + (cellFinalPosition.y - cellInitialPosition.y),
        });
      });
    }

    if (cellView.model.attributes.type === 'treetop.SelectionBehavior') {
      snapCenterOfElementToGrid(cellView.model as joint.dia.Element);
    }
  };

  private handleCellClicked = (cellView: joint.dia.CellView, event: joint.dia.Event) => {
    unselectAllLinks(this.graphSource.graph, this.paperSource.paper);

    if (cellView.model.attributes[ContainerAttributes.IsSubComponentArrow]) {
      const parent = cellView.model.getParentCell();
      const containerParent = parent.getParentCell();
      const embeds = containerParent.getEmbeddedCells().filter((el) => el.attributes.isContainerSubComponent);
      const embedIds = embeds.map((el) => el.id) as string[];
      const currentSubComponentIndex = embedIds.findIndex((el) => {
        return String(el) === String(parent.id);
      });

      let currentSubComponentId = '';
      let exchangeSubComponentId = '';

      if (cellView.model.attributes[ContainerAttributes.IsSubComponentUpArrow]) {
        currentSubComponentId = embedIds[currentSubComponentIndex];
        exchangeSubComponentId = embedIds[currentSubComponentIndex - 1];
      } else if (cellView.model.attributes[ContainerAttributes.IsSubComponentDownArrow]) {
        currentSubComponentId = embedIds[currentSubComponentIndex];
        exchangeSubComponentId = embedIds[currentSubComponentIndex + 1];
      }

      setTimeout(() => {
        this.flowchartLoaderService.exchangeContainerSubElementsInSubject(
          currentSubComponentId,
          exchangeSubComponentId,
          containerParent.id as string,
          this.flowchartElementsSubjectStore,
          this.operationsService,
        );
      }, 100);

      return;
    }

    if (!cellView.model) {
      cellView = this.paperSource.paper.findViewByModel(cellView.id);
    }

    if (cellView.model.attributes.isElementDescription) {
      cellView = this.paperSource.paper.findViewByModel(cellView.model.attributes.parentElementId);
    }

    if (cellView.model.attributes.isTextBlockContent || cellView.model.attributes.isElementTag) {
      cellView = this.paperSource.paper.findViewByModel(cellView.model.attributes.parent);
    }

    if (cellView.model.attributes.isHeader || cellView.model.attributes.isElementDescription) {
      const headerCellView = this.paperSource.paper.findViewByModel(cellView.model.attributes.parent);
      this.flowchartManagerService.setSelectedElementView(headerCellView as joint.dia.ElementView);
    } else {
      this.flowchartManagerService.setSelectedElementView(cellView as joint.dia.ElementView);
    }

    this.paperSource.paper.removeTools();
    joint.dia.HighlighterView.removeAll(this.paperSource.paper);
    this.selectedLink = null;

    this.selectedCell = !cellView?.model?.attributes?.isElementDescription
      ? cellView
      : this.paperSource.paper.findViewByModel(cellView.model.attributes.subflowParentId);

    joint.ui.FreeTransform.clear(this.paperSource.paper);

    this.clearSelection();

    if (
      cellView?.model?.attributes?.type !== 'standard.Link' &&
      !cellView?.model?.attributes?.isElementTag &&
      !cellView?.model.attributes[ContainerAttributes.IsSubComponentArrow]
    ) {
      if (
        cellView.model.attributes.isHeader ||
        cellView.model.attributes.isElementDescription ||
        cellView.model.attributes.isContainerSelectionBehavior
      ) {
        this.legacySidebarService.updateCurrentElement(
          this.flowchartId,
          cellView.model.attributes.parent,
          cloneDeep(this.flowchartElementsSubjectStore.getValue()),
        );
      } else {
        this.legacySidebarService.updateCurrentElement(
          this.flowchartId,
          cellView.model.attributes.id,
          cloneDeep(this.flowchartElementsSubjectStore.getValue()),
          cellView.model.attributes.isSubflow,
        );
      }
    }

    if (
      cellView.model.attributes.type !== 'standard.Link' &&
      !cellView.model.attributes.isHeader &&
      !cellView.model.attributes?.isElementTag &&
      !cellView.model.attributes[ContainerAttributes.IsSubComponentArrow]
    ) {
      // TODO: Verify if we should use .get('parent') or .attributes.subflowParentId
      const parent = cellView.model.get('parent');
      let resizeTool;
      const parentElement =
        this.paperSource.paper.model.getCell(parent) || this.paperSource.paper.model.getCell(cellView.model.attributes.parentElementId);
      let flowchartElement;

      if (!cellView.model.attributes.isContainerSubComponent) {
        if (parentElement) {
          const parentFromSubject = this.flowchartElementsSubjectStore.getValue().find((el) => el.id === parentElement.id);
          // Can be null in case of selection behavior in container
          if (parentFromSubject?.children) {
            flowchartElement = parentFromSubject.children.find((el) => el.id === cellView.model.id);
          }
        } else {
          flowchartElement = this.flowchartElementsSubjectStore.getValue().find((el) => el.id === cellView.model.attributes.id);
        }
      }

      if (cellView.model.attributes.startNode) {
        if (!flowchartElement.guidelineCriteria && !flowchartElement.guidelineAction) {
          const paperOffset = cellView.paper.$el.offset();
          const absolutePosX = cellView.getBBox().center().x + paperOffset.left;
          const absolutePosY = cellView.getBBox().topMiddle().y + paperOffset.top;
          const point: FlexibleConnectedPositionStrategyOrigin = {
            x: absolutePosX,
            y: absolutePosY,
            width: 0,
            height: 0,
          };

          const dtoElement = this.legacySidebarService.deepSearchElements(
            this.flowchartElementsSubjectStore.getValue(),
            cellView.model.attributes.id.toString(),
          );

          const popover = this.popoverService.open<MedicalComponentSearchPopoverData>(MedicalComponentSearchPopoverComponent, point, {
            width: 400,
            data: {
              definition: new FormControl(''),
              findingFilterActive: true,
              activityFilterActive: true,
              observationFilterActive: false,
              enableMultiSearch: true,
              isFilterSearch: false,
              element: dtoElement,
            },
            positionStrategy: 'center',
          });
          popover.afterClosed().subscribe((result) => {
            if (result) {
              this.flowchartManagerService.setSearchResult(result);
            }
          });
        }
      }

      const availableTools = this.createTools(parentElement, cellView, flowchartElement);

      if (!cellView.model.attributes.isContainerSubComponent && !cellView.model.attributes.isContainerSelectionBehavior) {
        if (flowchartElement?.elementType === FlowchartElementType.FlowchartSelectionBehavior && event.target.tagName === 'image') {
          this.selectionBehaviorService.toggleMenu(cellView.model as SelectionBehaviorElement, this.flowchartLoaderService);
        }
      }

      let tools: joint.dia.ToolsView;
      const deleteButton = availableTools.filter((tool) => tool.options.customType === 'delete-button')[0];

      if (cellView.model.attributes.isContainerSubComponent) {
        tools = new joint.dia.ToolsView({
          tools: [deleteButton],
        });
        cellView.addTools(tools);
      } else if (!cellView.model.attributes.isContainerSelectionBehavior) {
        const toolsListForStartNode = availableTools.filter((tool) => tool !== deleteButton);
        tools = new joint.dia.ToolsView({
          tools: flowchartElement?.elementType === FlowchartElementType.FlowchartStartNode ? toolsListForStartNode : availableTools,
        });

        if (cellView.model.attributes.isContainer) {
          // There are two embeds (header and selection behavior)
          resizeTool = ContainerResize.createContainerResizingTool(cellView, this.paperSource.paper);
          this.resizeTool = resizeTool;
        } else if (
          (flowchartElement?.elementType != FlowchartElementType.FlowchartSelectionBehavior && !cellView.model.attributes?.isSubflow) ||
          cellView.model.attributes.isContainerSelectionBehavior
        ) {
          // No resize for selection behavior
          resizeTool = this.sizeService.createResizingTool(cellView);
          this.resizeTool = resizeTool;
        }

        if (resizeTool) {
          resizeTool?.on({
            'resize:stop': (evt) => {
              const subflow = this.graphSource.graph.getCell(evt.data.cellView.model.attributes?.subflowParentId);
              if (subflow) {
                this.sizeService.resizeParentToFit(subflow as joint.dia.Element, this.graphSource.graph);
                const dto = this.legacySidebarService.deepSearchElements(
                  this.flowchartElementsSubjectStore.getValue(),
                  evt.data.cellView.model.attributes?.subflowParentId as string,
                );
                this.operationsService.addElementTags(dto);
              }
              this.flowchartLoaderService.setElementSizeInSubject(this.flowchartElementsSubjectStore, evt.data.cellView);
            },
            resize: (evt) => {
              const dto = this.legacySidebarService.deepSearchElements(
                this.flowchartElementsSubjectStore.getValue(),
                evt.data.cellView.model.id as string,
              );
              this.debouncedAddElementTags(dto);
              let knownMinTagSize = 0;

              if (evt.data.cellView.model.attributes.isContainer) {
                const embeds: string[] = evt.data.cellView.model.attributes.embeds;
                const tags = embeds.map((el) => this.graphSource.graph.getCell(el)).filter((el) => el.attributes.isElementTag);
                tags.forEach((el) => {
                  knownMinTagSize += el.attributes.size.width + FlowchartConfig.tag.padding.paddingBetweenTags;
                });
                knownMinTagSize += 2 * FlowchartConfig.tag.padding.horizontalPadding - FlowchartConfig.tag.padding.paddingBetweenTags;
              }

              let tagIndex = 0;
              let totalTagWidth = FlowchartConfig.tag.padding.horizontalPadding;
              this.graphSource.graph.getElements().filter((cell: joint.dia.Cell) => {
                if (evt.data.cellView.model.attributes.isTextBlock) {
                  const foreignObject = evt.data.cellView.model.getEmbeddedCells().find((embed) => embed.attributes?.isTextBlockContent);
                  foreignObject.prop('position', {
                    x: evt.data.cellView.model.attributes.position.x,
                    y: evt.data.cellView.model.attributes.position.y + FlowchartConfig.textBlockNode.offsetTop,
                  });
                  foreignObject.prop('attrs/foreignObject/width', evt.data.cellView.model.attributes.size.width);
                  foreignObject.prop(
                    'attrs/foreignObject/height',
                    evt.data.cellView.model.attributes.size.height - FlowchartConfig.textBlockNode.offsetTopWithPadding,
                  );
                  foreignObject.prop('attrs/body/width', evt.data.cellView.model.attributes.size.width);
                  foreignObject.prop(
                    'attrs/body/height',
                    evt.data.cellView.model.attributes.size.height - FlowchartConfig.textBlockNode.offsetTopWithPadding,
                  );
                }

                // TODO: Verify if we should use .get('parent') or .attributes.subflowParentId
                else if (cell.get('parent') === cellView.model.id) {
                  const cellAsElement = cell as joint.dia.Element;
                  if (cellAsElement.attributes.isElementTag) {
                    tagIndex += 1;
                    totalTagWidth += cellAsElement.attributes.size.width + FlowchartConfig.tag.padding.paddingBetweenTags;
                    if (cellView.model.attributes.size.width < totalTagWidth + FlowchartConfig.tag.padding.horizontalPadding) {
                      const parent = this.graphSource.graph.getCell(cellView.model.id) as joint.dia.Element;
                      parent.resize(totalTagWidth, cellView.model.attributes.size.height);
                    }
                  } else if (!cellAsElement.attributes.subflowParentId && !cellAsElement.attributes.isHeader) {
                    const subElementPositionX =
                      evt.data.cellView.model.attributes.position.x +
                      FlowchartConfig.containerNode.subComponentNode.styles.horizontalPadding;
                    const elementView = this.paperSource.paper.findViewByModel(cellAsElement.id);
                    const minPossibleWidth =
                      knownMinTagSize > evt.data.cellView.model.attributes.size.width
                        ? knownMinTagSize
                        : evt.data.cellView.model.attributes.size.width;

                    cellAsElement.resize(minPossibleWidth - 60, getHeightOfSubComponent(cellAsElement));
                    cellAsElement.set('position', { x: subElementPositionX, y: elementView.model.attributes.position.y });
                    if (cellAsElement.attributes.isContainerSelectionBehavior) {
                      this.handleSelectionBehaviorForContainer(cellView.model.id as string);
                    }

                    if (cellAsElement.attributes.isContainerSubComponent) {
                      cellAsElement
                        .getEmbeddedCells()
                        .filter((el) => el.attributes.isElementDescription)
                        .forEach((el) => {
                          const descriptionWidth = cellAsElement.attributes.size.width - 40;
                          const x = cellAsElement.attributes.position.x + (cellAsElement.attributes.size.width - descriptionWidth) / 2;
                          el.set('position', { x: x, y: el.attributes.position.y });
                        });
                    }

                    if (cellAsElement.attributes.isElementDescription) {
                      const mainElement = this.graphSource.graph.getCell(cellAsElement.attributes.parentElementId);
                      const hasTags = mainElement?.getEmbeddedCells()?.find((embed) => embed.attributes.isElementTag);
                      const descriptionWidth = mainElement.attributes.size.width - 40;
                      const x = mainElement.attributes.position.x + (mainElement.attributes.size.width - descriptionWidth) / 2;
                      const y = hasTags ? mainElement.attributes.position.y + 55 : mainElement.attributes.position.y + 45;
                      cellAsElement.set('position', { x: x, y: getGridMatchedPositionValue(y) });
                    }
                  }
                }
              });
            },
          });

          resizeTool.render();
        }
      }

      cellView.addTools(tools);
    }
  };

  private handleLinkPointerClick = (linkView: joint.dia.LinkView, allowComments: boolean) => {
    this.paperSource.paper.removeTools();
    this.clearSelection();
    joint.dia.HighlighterView.removeAll(this.paperSource.paper);
    this.legacySidebarService.updateCurrentElementForLink(this.flowchartId, linkView.model.id as string);
    this.selectedCell = null;
    const snapAnchor = function (coords, endView, endMagnet) {
      const bbox = endView.getNodeUnrotatedBBox(endMagnet);
      const angle = endView.model.angle();
      const origin = endView.model.getBBox().center();
      coords.rotate(origin, angle);

      let anchor;
      const side = bbox.sideNearestToPoint(coords);
      switch (side) {
        case 'left':
          anchor = bbox.leftMiddle();
          break;
        case 'right':
          anchor = bbox.rightMiddle();
          break;
        case 'top':
          anchor = bbox.topMiddle();
          break;
        case 'bottom':
          anchor = bbox.bottomMiddle();
          break;
      }

      return { anchor: anchor.rotate(origin, -angle), anchorName: side };
    };

    function getDistance(pointA: joint.g.Point, pointB: joint.g.Point) {
      const dx = pointA.x - pointB.x;
      const dy = pointA.y - pointB.y;

      // -4 is needed because CustomConnector causes 8px loss on each side of the link.
      return parseFloat((Math.sqrt(Math.abs(dx * dx - dy * dy)) - 4).toFixed(1));
    }

    function getVerticeDirection(pointA: joint.g.Point, pointB: joint.g.Point): 'horizontal' | 'vertical' {
      if (pointA.x === pointB.x) {
        return 'vertical';
      }
      if (pointA.y === pointB.y) {
        return 'horizontal';
      }
    }

    const getCustomTools = (): joint.dia.ToolView[] => {
      const customTools = [];
      const attributes = linkView.model.attributes;
      const vertices = attributes.vertices;
      let totalDistance = 0;
      //@ts-ignore
      let prevVertice = linkView.sourcePoint;
      if (vertices?.length > 0) {
        for (let i = 0; i < vertices.length; i++) {
          const vertice = vertices[i] as joint.g.Point;
          if (i > 0) {
            prevVertice = vertices[i - 1] as joint.g.Point;
          }

          // Between
          const pointDistance = getDistance(prevVertice, vertice);
          if (pointDistance > 50) {
            const tool = LinkToolGenerator.getLinkAdjusterTool(
              this.flowchartElementsSubjectStore,
              this.flowchartLoaderService,
              this.graphSource.graph,
              this.legacySidebarService,
            );
            tool.options.distance = totalDistance + getDistance(prevVertice, vertice) / 2;
            tool.options.direction = getVerticeDirection(vertice, prevVertice);
            tool.options.relatedVerticeIndex = i;
            customTools.push(tool);
          }
          totalDistance = totalDistance + pointDistance;

          if (i === vertices.length - 1) {
            //@ts-ignore
            if (getDistance(vertice, linkView.targetPoint) > 100) {
              const tool = LinkToolGenerator.getLinkAdjusterTool(
                this.flowchartElementsSubjectStore,
                this.flowchartLoaderService,
                this.graphSource.graph,
                this.legacySidebarService,
              );
              //@ts-ignore
              tool.options.distance = totalDistance + getDistance(vertice, linkView.targetPoint) / 2;
              //@ts-ignore
              tool.options.direction = getVerticeDirection(linkView.targetPoint, vertice);
              tool.options.relatedVerticeIndex = i + 1;
              customTools.push(tool);
            }
          }
        }
      }

      return customTools;
    };

    const customTools = getCustomTools();

    const handleAnchorChange = (coords, endView, endMagnet, endType, linkView: joint.dia.LinkView) => {
      const result = snapAnchor(coords, endView, endMagnet);
      let prevAnchor;

      if (endType === 'source') {
        prevAnchor = linkView.model.attributes.source.anchor.name;
      } else {
        prevAnchor = linkView.model.attributes.target.anchor.name;
      }

      if (result.anchorName !== prevAnchor) {
        this.flowchartLoaderService.setLinkAnchor(
          this.flowchartElementsSubjectStore,
          linkView.model.attributes.id,
          result.anchorName,
          endType,
        );

        this.linkTools.options.tools.forEach((tool) => {
          if (tool.options.markup && tool.options.markup.length > 0) {
            tool.el.remove();
            if (tool.options.markup[0].className === 'custom-link-adjuster') {
              tool.remove();
            }
          } else {
            tool.el.remove();
          }
        });
      }

      linkView.model.set('editVertices', false);
      linkView.model.set('manual', false);

      this.flowchartLoaderService.setManualLink(this.flowchartElementsSubjectStore, linkView.model.attributes.id, false);
      this.flowchartLoaderService.setSubjectLinkVertices(
        this.flowchartElementsSubjectStore,
        this.graphSource.graph.getCell(linkView.model.attributes.source.id),
        linkView.model.attributes.id,
        linkView.model.attributes.vertices,
      );

      return result.anchor;
    };

    const toolsView = new joint.dia.ToolsView({
      tools: [
        new joint.linkTools.TargetAnchor({
          snap: function (coords, endView, endMagnet, endType, linkView) {
            return handleAnchorChange(coords, endView, endMagnet, endType, linkView);
          },
          resetAnchor: false,
        }),
        new joint.linkTools.SourceAnchor({
          snap: function (coords, endView, endMagnet, endType, linkView) {
            return handleAnchorChange(coords, endView, endMagnet, endType, linkView);
          },
          resetAnchor: false,
        }),
        ...customTools,
        LinkToolGenerator.getResetTool(this.flowchartElementsSubjectStore, this.graphSource.graph, this.flowchartLoaderService),
        new joint.linkTools.Remove({
          action: () => {
            this.handleDeleteSelectedLink();
          },
          offset: 24,
          distance: 24,
        }),
      ],
    });

    toolsView.el.classList.add('link__tools');
    this.linkTools = toolsView;
    linkView.addTools(toolsView);
    const { stroke: StrokeHighlighter } = joint.highlighters;
    const strokeHighlighter = StrokeHighlighter.add(linkView, 'root', 'selection', {
      layer: joint.dia.Paper.Layers.FRONT,
    });
    strokeHighlighter.el.classList.add('link__selection');
    markLinkAsSelected(linkView);
    this.selectedLink = linkView.model;

    if (allowComments) {
      this.handleCommentPopover(linkView);
    }
  };

  private handleLinkConnect = (linkView: joint.dia.LinkView, _, newCellView: joint.dia.CellView) => {
    const sourceView: joint.dia.ElementView = linkView.model.getSourceElement().findView(this.paperSource.paper) as joint.dia.ElementView;
    if (
      (!sourceView.model.attributes?.parent && !newCellView.model.attributes?.parent) ||
      (sourceView.model.attributes?.parent &&
        newCellView.model.attributes?.parent &&
        sourceView.model.attributes?.parent === newCellView.model.attributes?.parent)
    ) {
      this.flowchartLoaderService.addPortLinkToSubject(this.flowchartElementsSubjectStore, sourceView, linkView.model);
    }

    const sourceElement = findFlowchartElementRecursive(this.flowchartElementsSubjectStore.getValue(), sourceView.model.attributes.id);

    if (sourceElement.elementType === FlowchartElementType.FlowchartDecisionNode) {
      this.operationsService.setLinkAttribute(null, linkView.model.attributes.id);
    }
  };

  private handleCellPointerDown = (cellView: joint.dia.CellView, event: joint.dia.Event) => {
    this.stopInteractionIfGestureDetected({ cellView, event });

    if (cellView.model.attributes.type === 'example.ForeignObject') {
      cellView.model.set('descriptionClicked', true);

      if (event.target.attributes['data-element-id']) {
        cellView.model.set('anchorLinkClicked', true);
        this.linksService.openAnchorLinkFromTextEditor(event.target as HTMLElement);
      }
    }
  };

  private handleCellPointerUp = (cellView: joint.dia.CellView) => {
    if (cellView.model.attributes.type === 'example.ForeignObject') {
      cellView.model.set('descriptionClicked', false);
      cellView.model.set('anchorLinkClicked', false);
    }
  };

  private handleBlankPointerDown = (event: joint.dia.Event) => {
    this.stopInteractionIfGestureDetected({ event });

    this.scrollerSource.startPanning(event);

    joint.ui.TextEditor.close();
  };

  private handleBlankPointerClick = () => {
    this.paperSource.paper.removeTools();

    this.clearSelection();

    joint.dia.HighlighterView.removeAll(this.paperSource.paper);
    unselectAllLinks(this.graphSource.graph, this.paperSource.paper);

    this.selectedLink = null;
    this.selectedCell = null;

    this.selectionService.selectWidget(this.flowchartId);
  };

  private handleElementPointerDown = (cellView: joint.dia.CellView, event: joint.dia.Event) => {
    this.paperSource.paper.removeTools();

    this.stopInteractionIfGestureDetected({ cellView, event });

    if (this.currentElementActivityToolbarRef) {
      this.currentElementActivityToolbarRef.remove();
    }

    if (
      cellView.model.attributes.isHeader ||
      cellView.model.attributes.isContainerSubComponent ||
      cellView.model.attributes.isElementTag ||
      cellView.model.attributes.isContainerSelectionBehavior ||
      cellView.model.attributes[ContainerAttributes.IsSubComponentArrow]
    ) {
      cellView.preventDefaultInteraction(event);
    }

    if (cellView.model.attributes.isElementDescription) {
      const parentElementId = cellView.model.attributes.parentElementId;
      if (parentElementId) {
        const parentCellView = this.paperSource.paper.findViewByModel(parentElementId);
        if (parentCellView?.model.attributes.isContainerSubComponent) {
          cellView.preventDefaultInteraction(event);
        }
      }
    }
  };

  private handleElementPointerMove = (elementView: joint.dia.ElementView, event) => {
    this.showPorts(false, elementView.model);
  };

  private handleElementMagnetPointerDown = (elementView: joint.dia.ElementView) => {
    this.paperSource.paper.model.getElements().forEach((cell) => {
      const cellViewS = this.paperSource.paper.findViewByModel(elementView.model.id);
      const cellViewT = this.paperSource.paper.findViewByModel(cell.attributes.id);

      if (cell.attributes.isHeader || cell.attributes.isContainerSubComponent) {
        return;
      }

      if (!LinkValidator.doesLinkConnectInsideElementWithSubflowElement(cellViewS, cellViewT)) {
        return;
      }

      this.showPorts(true, cell);
    });
  };

  private handleElementMagnetPointerUp = () => {
    this.paperSource.paper.model.getElements().forEach((cell) => {
      this.showPorts(false, cell);
    });
  };

  private handleElementMouseEnter = (element: joint.dia.CellView) => {
    this.showPorts(true, element.model);
  };

  private handleElementMouseLeave = (element: joint.dia.CellView) => {
    this.showPorts(false, element.model);
  };

  private handleElementPointerClick = (cellView: joint.dia.CellView, allowComments: boolean) => {
    this.flowchartSearchService.adjustFilters(cellView);

    if (allowComments) {
      this.handleCommentPopover(cellView);
    }
  };

  private handleElementPointerUp = (elementView: joint.dia.ElementView, event: joint.dia.Event) => {
    if (elementView.model.attributes[ContainerAttributes.IsSubComponentArrow]) {
      return;
    }

    this.scrollerSource.stopScrollWhileDragging(event);

    this.showPorts(true, elementView.model);

    this.paperSource.paper.setInteractivity({ stopDelegation: true, labelMove: true, linkMove: false, elementMove: true });
    if (elementView.model.attributes.isTextBlockContent || elementView.model.attributes.isElementDescription) {
      elementView = this.paperSource.paper.findViewByModel(elementView.model.attributes.parent);
    }

    //update position for subelements
    this.flowchartLoaderService.setElementPositionInSubject(
      this.flowchartElementsSubjectStore,
      elementView,
      this.paperSource.paper,
      this.graphSource.graph,
    );

    // Allow resize for text blocks
    if (elementView.model.attributes.showTextEditor) {
      // TODO: Use resizing service after merge
      const resizeTool = this.sizeService.createResizingTool(elementView);

      resizeTool.on('resize:stop', (_evt) => {
        this.flowchartLoaderService.setElementSizeInSubject(this.flowchartElementsSubjectStore, elementView);
      });
      resizeTool.render();
    }

    if (elementView.model.attributes?.isSubflow) {
      this.paperSource.paper.removeTools();
    }

    if (elementView.model.attributes?.subflowParentId) {
      const subflowElement = this.legacySidebarService.deepSearchElements(
        this.flowchartElementsSubjectStore.getValue(),
        elementView.model.attributes?.subflowParentId as string,
      );
      const subflow = this.graphSource.graph.getCell(elementView.model.attributes?.subflowParentId);
      subflow.set('allowSubflowManualLinkHandling', false);
      this.sizeService.resizeParentToFit(subflow as joint.dia.Element, this.graphSource.graph);
      this.operationsService.addElementTags(subflowElement);
      const subflowView = this.paperSource.paper.findViewByModel(
        this.graphSource.graph.getCell(elementView.model.attributes.subflowParentId as string),
      );
      this.flowchartLoaderService.setElementPositionInSubject(
        this.flowchartElementsSubjectStore,
        subflowView as joint.dia.ElementView,
        this.paperSource.paper,
        this.graphSource.graph,
      );
    }
  };

  private handleMouseOver = (cellView: joint.dia.CellView) => {
    if (cellView.model.attributes[ContainerAttributes.IsSubComponentArrow]) {
      const parentSubComponent = cellView.model.getParentCell() as joint.dia.Element;
      const embedded = parentSubComponent.getEmbeddedCells().filter((cell) => cell.attributes[ContainerAttributes.IsSubComponentArrow]);
      const container = parentSubComponent.getParentCell();
      const containerSubComponentCells = container.getEmbeddedCells().filter((cell) => cell.attributes[ContainerAttributes.IsSubComponent]);
      const subComponentIndex = containerSubComponentCells.map((cell) => cell.id).indexOf(parentSubComponent.id);
      embedded.forEach((cell: joint.dia.Element) => {
        if (
          !(subComponentIndex === 0 && cell.attributes[ContainerAttributes.IsSubComponentUpArrow]) &&
          !(subComponentIndex === containerSubComponentCells.length - 1 && cell.attributes[ContainerAttributes.IsSubComponentDownArrow])
        ) {
          this.paperSource.paper.findViewByModel(cell).$el.show();
        }

        if (
          (subComponentIndex === 0 && cell.attributes[ContainerAttributes.IsSubComponentDownArrow]) ||
          (subComponentIndex === containerSubComponentCells.length - 1 && cell.attributes[ContainerAttributes.IsSubComponentUpArrow])
        ) {
          const position = ContainerResize.getSubComponentArrowRightPosition(parentSubComponent, cell);
          cell.position(position.x, position.y);
        }
      });
    } else if (cellView.model.attributes[ContainerAttributes.IsSubComponent]) {
      const embedded = cellView.model.getEmbeddedCells().filter((cell) => cell.attributes[ContainerAttributes.IsSubComponentArrow]);
      const container = cellView.model.getParentCell();
      const containerSubComponentCells = container.getEmbeddedCells().filter((cell) => cell.attributes[ContainerAttributes.IsSubComponent]);
      const subComponentIndex = containerSubComponentCells.map((cell) => cell.id).indexOf(cellView.model.id);
      embedded.forEach((cell: joint.dia.Element) => {
        if (
          !(subComponentIndex === 0 && cell.attributes[ContainerAttributes.IsSubComponentUpArrow]) &&
          !(subComponentIndex === containerSubComponentCells.length - 1 && cell.attributes[ContainerAttributes.IsSubComponentDownArrow])
        ) {
          this.paperSource.paper.findViewByModel(cell).$el.show();
        } else {
          this.paperSource.paper.findViewByModel(cell).$el.hide();
        }

        if (
          (subComponentIndex === 0 && cell.attributes[ContainerAttributes.IsSubComponentDownArrow]) ||
          (subComponentIndex === containerSubComponentCells.length - 1 && cell.attributes[ContainerAttributes.IsSubComponentUpArrow])
        ) {
          const position = ContainerResize.getSubComponentArrowRightPosition(cellView.model as joint.dia.Element, cell);
          cell.position(position.x, position.y);
        }
      });
    }
  };

  private handleMouseOut = (cellView: joint.dia.CellView) => {
    if (cellView.model.attributes[ContainerAttributes.IsSubComponent]) {
      const embedded = cellView.model.getEmbeddedCells().filter((cell) => cell.attributes[ContainerAttributes.IsSubComponentArrow]);
      embedded.forEach((cell) => {
        this.paperSource.paper.findViewByModel(cell).$el.hide();
      });
    }
  };

  private handleSelectionBehaviorOpen = (cellView: SelectionBehaviorElementView) => {
    this.selectionBehaviorService.toggleMenu(cellView.model, this.flowchartLoaderService);
  };

  private handleSelectionBehaviorClose = (cellView: SelectionBehaviorElementView) => {
    this.selectionBehaviorService.toggleMenu(cellView.model, this.flowchartLoaderService);
  };

  private handleLinkPointerUp = (linkView: joint.dia.LinkView) => {
    const newLabelPositions = AttributePositionLogic.getLabelPositions(linkView);
    this.flowchartLoaderService.setSubjectLinkLabelPosition(this.flowchartElementsSubjectStore, linkView, newLabelPositions);
  };

  private handleSelectionBehaviorForContainer(id: string) {
    const flowchartElements = this.flowchartElementsSubjectStore.getCloneDeepValue();
    const containerNode = this.legacySidebarService.deepSearchElements(flowchartElements, id);
    if (containerNode) {
      this.operationsService.updateSelectionBehaviorForSubActions(containerNode);
    }
  }

  private openAddToolbarPopover(cellView: joint.dia.CellView, addButtonParameters: AddButtonOptions) {
    window.setTimeout(() => {
      const paperOffset = cellView.paper.$el.offset();
      const absolutePosX = cellView.getBBox().center().x + paperOffset.left;
      let absolutePosY = cellView.getBBox().topMiddle().y + paperOffset.top;

      if (cellView.model.attributes.isSubflow && !addButtonParameters.isSubflow) {
        absolutePosY = cellView.getBBox().bottomMiddle().y + paperOffset.top;
      }

      const point: FlexibleConnectedPositionStrategyOrigin = {
        x: absolutePosX,
        y: absolutePosY,
        width: 0,
        height: 0,
      };
      const { element, position1, position2, isSubflow, subflowLinkedElement } = addButtonParameters;
      const popover = this.popoverService.open(AddToolbarPopoverComponent, point, {
        width: 400,
        panelClass: 'add-toolbar-popover',
        data: {
          testData: 'testData',
          addButtonParameters: addButtonParameters,
        },
        positionStrategy: 'center',
      });
      popover.afterClosed().subscribe((result) => {
        if (result) {
          if (addButtonParameters.isReplaceElement) {
            this.elementActivitiesTransactionService.handleConfirmationDialog(result, element);
          } else {
            this.nodePickerTransactionService.handleNodePickerEventCreateNode(result, {
              sourceElement: element as joint.dia.ElementView,
              sourceConnector: position1,
              targetConnector: position2,
              subflow: isSubflow,
              subflowLinkedElement,
            });
            this.selectedElement = element;
          }
        }
      });
    }, 250);
  }

  private openSearchPopover(action: joint.dia.Cell) {
    const newElementView = this.paperSource.paper.model.getElements().find((el) => el.id === action.id);
    const dtoElement = this.legacySidebarService.deepSearchElements(this.flowchartElementsSubjectStore.getValue(), action.id.toString());

    this.scrollerSource.scrollToElement(newElementView, { animation: { duration: 200 } });
    window.setTimeout(() => {
      const cellView = this.paperSource.paper.findViewByModel(action.id);
      const paperOffset = cellView.paper.$el.offset();
      const absolutePosX = cellView.getBBox().center().x + paperOffset.left;
      const absolutePosY = cellView.getBBox().topMiddle().y + paperOffset.top;
      const point: FlexibleConnectedPositionStrategyOrigin = {
        x: absolutePosX,
        y: absolutePosY,
        width: 0,
        height: 0,
      };

      const popover = this.popoverService.open<MedicalComponentSearchPopoverData>(MedicalComponentSearchPopoverComponent, point, {
        width: 600,
        data: {
          definition: new FormControl(''),
          findingFilterActive: true,
          activityFilterActive: true,
          observationFilterActive: true,
          enableMultiSearch: true,
          isFilterSearch: false,
          element: dtoElement,
          isDecisionNode: false,
        },
        positionStrategy: 'center',
      });
      popover.afterClosed().subscribe((result) => {
        this.flowchartManagerService.setSearchResult(result);
      });
    }, 250);
  }

  private subscribeToComponentAddedExecution() {
    this.flowchartSearchService.getComponentAddedObservable().subscribe(() => {
      if (!this.selectedElement) {
        this.selectedElement = this.flowchartManagerService.getSelectedElementView();
      }
      if (this.selectedElement) {
        let flowchartElement;
        if (!this.selectedElement.model.attributes.subflowParentId) {
          flowchartElement = this.flowchartElementsSubjectStore.getValue().find((el) => el.id === this.selectedElement.model.id);
        } else {
          this.flowchartElementsSubjectStore.getValue().forEach((element) => {
            if (element?.children?.find((el) => el.id === this.selectedElement.model.id)) {
              flowchartElement = element.children.find((el) => el.id === this.selectedElement.model.id);
            }
          });
        }
      }
    });
  }

  private showPorts(show: boolean, cell: joint.dia.Cell) {
    const value = show ? 'visible' : 'hidden';
    cell.prop('ports/groups/left/attrs/portLeft/visibility', value);
    cell.prop('ports/groups/right/attrs/portRight/visibility', value);
    cell.prop('ports/groups/top/attrs/portTop/visibility', value);
    cell.prop('ports/groups/bottom/attrs/portBottom/visibility', value);
  }

  private ctrlPlusEventHandler(event: any) {
    event.preventDefault();
    this.scrollerSource.zoom(FlowchartConfig.paper.ZOOM_STEP, { max: FlowchartConfig.paper.ZOOM_MAX });
  }

  private ctrlMinusEventHandler(event: any) {
    event.preventDefault();
    this.scrollerSource.zoom(-FlowchartConfig.paper.ZOOM_STEP, { min: FlowchartConfig.paper.ZOOM_MIN });
  }

  private touchMoveEventHandler = (event: TouchEvent): void => {
    if (event.touches.length > 1) {
      event.preventDefault();
    }
  };

  private handleDeleteSelectedLink(): boolean {
    if (!this.selectedLink) {
      return false;
    }

    const confirmationDialogArgs: ConfirmationDialogArgs = {
      message: 'Flowchart.ConfirmationDialog.RemoveLinks.Message',
      cancelButtonCaption: 'Cancel',
      confirmButtonCaption: 'Delete',
    };

    if (!this.linkDeleteConfirmationDialgRef) {
      this.linkDeleteConfirmationDialgRef = this.dialogService.open(ConfirmationDialogComponent, {
        icon: 'warning',
        title: 'Flowchart.ConfirmationDialog.RemoveLinks.Title',
        data: confirmationDialogArgs,
      });

      this.linkDeleteConfirmationDialgRef.afterClosed().subscribe((isConfirmed: boolean) => {
        this.linkDeleteConfirmationDialgRef = null;

        if (!isConfirmed || !this.selectedLink) {
          return;
        }

        this.selectedLink.remove();
        this.flowchartLoaderService.removeLinkFromSubject(this.flowchartElementsSubjectStore, this.selectedLink);
        this.selectedLink = null;
      });
    }

    return true;
  }

  private keydownEventHandler = (event: KeyboardEvent) => {
    if (this.isKeyboardDeleteKey(event) && this.selectedLink) {
      const activeElement = document.activeElement;
      const inputs = ['input', 'select', 'textarea', 'mat-option', 'mat-select', 'div'];
      if (activeElement && inputs.indexOf(activeElement.tagName.toLowerCase()) === -1) {
        this.handleDeleteSelectedLink();
      }
    }
  };

  private secondaryKeyDownEventHandler = (event: KeyboardEvent) => {
    if (this.selectedCell && this.isKeyboardDeleteKey(event)) {
      const activeElement = document.activeElement;
      const inputs = ['input', 'select', 'textarea', 'mat-option', 'mat-select', 'div'];
      if (!this.selectedCell.model.attributes.startNode && activeElement && inputs.indexOf(activeElement.tagName.toLowerCase()) === -1) {
        this.removeCell(this.selectedCell);
      }
    }
  };

  private isKeyboardDeleteKey(keyboardEvent: KeyboardEvent): boolean {
    return (
      keyboardEvent.key === 'Delete' || keyboardEvent.key === 'Backspace' || (keyboardEvent.metaKey && keyboardEvent.key === 'Backspace')
    );
  }

  private removeCell(_cellView) {
    if (this.dialog.openDialogs.length === 0) {
      const element = this.legacySidebarService.deepSearchElements(
        this.flowchartElementsSubjectStore.getCloneDeepValue(),
        _cellView.model.attributes.id,
      );
      //use uniqueId if available, otherwise use id (eg for selection behavior)
      const id = element.uniqueId ? element.uniqueId : element.id;

      if (!id) {
        return;
      }

      const elementsLinkingToThisItem = this.linksService.getAllElementsLinkingToThisItem(element);

      const confirmationDialog = this.dialogService.open(ConfirmationDialogComponent, {
        title: 'Flowchart.ConfirmationDialog.RemoveElement.Title',
        icon: 'triangle-alert',
        data: {
          message: elementsLinkingToThisItem.length > 0 ? 'ActiveLinkFoundMessage' : 'Flowchart.ConfirmationDialog.RemoveElement.Message',
          cancelButtonCaption: 'Cancel',
          confirmButtonCaption: 'Delete',
        },
        disableClose: true,
      });

      confirmationDialog.afterClosed().subscribe((isConfirmed: boolean) => {
        if (!isConfirmed || !this.selectedCell) {
          return;
        }

        this.guidelineService.removeLinksByTargets([element.id]);

        const connectedLinks = this.graphSource.graph.getConnectedLinks(_cellView.model);
        const firstLink = connectedLinks[0];
        if (firstLink) {
          const connectedElementId =
            firstLink.attributes.target.id === _cellView.model.id ? firstLink.attributes.source.id : firstLink.attributes.target.id;
          const connectedElement = this.paperSource.paper.model.getElements().find((el) => el.id == connectedElementId);
          //scroll to connected element if it is not visible after deletion (bug #3320)
          if (!this.scrollerSource.isElementVisible(connectedElement)) {
            this.scrollerSource.scrollToElement(connectedElement, { animation: { duration: 200 } });
          }
        }

        this.flowchartLoaderService.removeElementAndAdjustLinksInSubject(
          _cellView.model,
          this.flowchartElementsSubjectStore,
          this.operationsService,
        );

        if (_cellView.model.attributes.isContainerSubComponent) {
          this.handleSelectionBehaviorForContainer(_cellView.model.attributes.parentContainerId);
        }

        this.selectedCell = null;
        this.flowchartSidebarStoreService.setDto(null);
      });
    }
  }

  private handleButtonClick(
    element: joint.dia.CellView,
    position1,
    position2,
    isReplaceElement: boolean,
    parentElement: joint.dia.Cell,
    cellView: joint.dia.CellView,
  ): void {
    const isSubflow = Boolean(parentElement);

    this.setScrollIntervalForToolbar();

    let addButtonParameters: AddButtonOptions;

    if (isReplaceElement) {
      addButtonParameters = {
        element: cellView,
        position1,
        position2,
        isSubflow,
        subflowLinkedElement: false,
        isReplaceElement,
      };
    } else {
      addButtonParameters = {
        element,
        position1,
        position2,
        isSubflow,
        subflowLinkedElement: isSubflow,
        isReplaceElement,
      };
    }

    this.openAddToolbarPopover(cellView, addButtonParameters);
    this.resizeTool?.remove();

    if (this.toolbarScrollInterval) {
      clearInterval(this.toolbarScrollInterval);
    }
  }

  private setActive(buttonActive: joint.elementTools.Button, buttonsInactive: joint.elementTools.Button[]) {
    buttonActive.$el.children()[0].setAttribute('fill', FlowchartConfig.plus.active);
    buttonsInactive.forEach((btn) => {
      btn.$el.children()[0].setAttribute('fill', FlowchartConfig.plus.inactive);
    });
  }

  private createTools(parentElement: joint.dia.Cell, cellView: joint.dia.CellView, flowchartElement: FlowchartGenericDto) {
    const basicTools = this.createBasicTools(parentElement, cellView);
    const advancedTools = this.createAdvancedTools(parentElement, cellView, flowchartElement);

    return [...basicTools, ...advancedTools];
  }

  private createBasicTools(parentElement: joint.dia.Cell, cellView: joint.dia.CellView): joint.elementTools.Button[] {
    const leftButton = generateLeftButton();
    leftButton.options.action = (_, elementView) => {
      this.handleButtonClick(elementView, { name: 'left' }, { name: 'right' }, false, parentElement, cellView);
      this.setActive(leftButton, [bottomButton, rightButton, topButton]);
    };

    const rightButton = generateRightButton();
    rightButton.options.action = (_, elementView) => {
      this.handleButtonClick(elementView, { name: 'right' }, { name: 'left' }, false, parentElement, cellView);
      this.setActive(rightButton, [bottomButton, leftButton, topButton]);
    };

    const bottomButton = generateBottomButton();
    bottomButton.options.action = (_, elementView) => {
      this.handleButtonClick(elementView, { name: 'bottom' }, { name: 'top' }, false, parentElement, cellView);
      this.setActive(bottomButton, [leftButton, rightButton, topButton]);
    };

    const deleteButton = new joint.elementTools.Remove({
      focusOpacity: 0.5,
      x: '0%',
      y: '0%',
      offset: { x: -FlowchartConfig.removeButton.offset, y: -5 },
      action: () => {
        this.removeCell(cellView);
      },
      customType: 'delete-button',
    });

    if (cellView?.model.attributes.startNode) {
      return [leftButton, rightButton, bottomButton, deleteButton];
    }

    const topButton = generateTopButton();
    topButton.options.action = (_, elementView) => {
      this.handleButtonClick(elementView, { name: 'top' }, { name: 'bottom' }, false, parentElement, cellView);
      this.setActive(topButton, [leftButton, rightButton, bottomButton]);
    };

    return [leftButton, rightButton, bottomButton, topButton, deleteButton];
  }

  private createAdvancedTools(
    parentElement: joint.dia.Cell,
    cellView: joint.dia.CellView,
    flowchartElement: FlowchartGenericDto,
  ): joint.elementTools.Button[] {
    const advancedTools = [];

    if (cellView.model.attributes.startNode && cellView.model.attributes) {
      if (flowchartElement.guidelineCriteria || flowchartElement.guidelineAction) {
        const resetButtonStart = generateResetButtonStart(
          this.translocoService.translate('Flowchart.ElementActivitiesToolbar.Tooltip.ResetComponent'),
        );
        resetButtonStart.options.action = () => {
          this.elementActivitiesTransactionService.handleConfirmationDialog_StartNode(cellView);
        };

        advancedTools.push(resetButtonStart);
      }
    } else {
      const resetButtonRegular = generateResetButtonRegular(
        cellView,
        this.translocoService.translate('Flowchart.ElementActivitiesToolbar.Tooltip.ChangeComponent'),
      );
      resetButtonRegular.options.action = (_, elementView) => {
        this.handleButtonClick(elementView, { name: 'bottom' }, { name: 'top' }, true, parentElement, cellView);
      };

      advancedTools.push(resetButtonRegular);
    }

    if (cellView.model.attributes.isContainer) {
      const addButton = generateContainerAddButton();
      addButton.options.action = (_, elementView) => {
        const newSubComponent = ContainerNodeService.createContainerSubComponent(this.paperSource.paper, this.graphSource.graph, {
          text: '',
          size: cellView.model.attributes.size.width,
          type: FlowchartElementType.FlowchartAction,
          allowEvents: true,
        });

        newSubComponent.set('parentContainerId', cellView.model.attributes.id);
        this.operationsService.addSubComponentToContainer(cellView.model.attributes.id, newSubComponent);
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-type-assertion
        const newSubComponentElementView = this.paperSource.paper.findViewByModel(newSubComponent) as joint.dia.ElementView;
        this.flowchartManagerService.setSelectedElementView(newSubComponentElementView);
        this.flowchartLoaderService.addSubComponentToContainerInSubject(this.flowchartElementsSubjectStore, newSubComponent, cellView);
        this.legacySidebarService.updateCurrentElement(
          this.flowchartId,
          newSubComponent.id as string,
          this.flowchartElementsSubjectStore.getValue(),
        );
        this.openSearchPopover(newSubComponent);

        if (cellView.model.attributes?.subflowParentId) {
          this.sizeService.resizeParentToFit(
            this.graphSource.graph.getCell(cellView.model.attributes.subflowParentId) as joint.dia.Element,
            this.graphSource.graph,
          );
        }
        this.handleSelectionBehaviorForContainer(newSubComponent.attributes.parentContainerId);
      };

      advancedTools.push(addButton);
    }

    if (cellView.model.attributes.isSubflow) {
      const subflowAddButton = generateSubflowAddButton();
      subflowAddButton.options.action = (_, elementView) => {
        const previousPosition = elementView.model.attributes.position;
        const addButtonParameters: AddButtonOptions = {
          element: elementView,
          position1: { name: 'bottom' },
          position2: { name: 'top' },
          isSubflow: true,
          isReplaceElement: false,
          subflowLinkedElement: false,
          previousPosition: previousPosition,
        };
        this.openAddToolbarPopover(cellView, addButtonParameters);
      };

      advancedTools.push(subflowAddButton);
    }

    return advancedTools;
  }
}
